import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, TrendingUp, Calendar, User, Link2, Check, Share2, X } from 'lucide-react';
import { getCategoriesForPosition, getCategoryScore, getOverallScore } from '../data/criteria';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import VideoCard from '../components/VideoCard';
import VideoPlayer from '../components/VideoPlayer';
import RadarChart from '../components/RadarChart';
import ProgressChart from '../components/ProgressChart';
import { PlayersAPI, EvaluationsAPI, VideosAPI, InvitesAPI } from '../api';
import { useToast } from '../hooks/use-toast';

export default function PlayerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeVideo, setActiveVideo] = useState(null);
  const [player, setPlayer] = useState(null);
  const [evals, setEvals] = useState([]);
  const [videos, setVideos] = useState([]);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const copyInviteLink = async () => {
    try {
      const inv = await InvitesAPI.create(id);
      const url = window.location.origin + inv.url_path;
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast({ title: 'Invite link copied', description: 'Share it with the athlete or family.' });
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast({ title: 'Could not create invite', description: e?.response?.data?.detail || 'Try again.' });
    }
  };

  const copyProgressShare = async () => {
    try {
      const res = await PlayersAPI.shareProgress(id);
      const url = window.location.origin + res.url_path;
      await navigator.clipboard.writeText(url);
      toast({ title: 'Progress link copied', description: 'Read-only. Great to send to recruiters.' });
    } catch (e) {
      toast({ title: 'Could not create link', description: e?.response?.data?.detail || 'Try again.' });
    }
  };

  const revokeProgressShare = async () => {
    try {
      const res = await PlayersAPI.revokeProgress(id);
      toast({
        title: res.revoked > 0 ? 'Share link revoked' : 'No active link',
        description: res.revoked > 0 ? 'The old link no longer works. Generate a new one when you\'re ready.' : 'There was no active progress link to revoke.',
      });
    } catch (e) {
      toast({ title: 'Could not revoke', description: e?.response?.data?.detail || 'Try again.' });
    }
  };

  useEffect(() => {
    PlayersAPI.get(id).then(setPlayer).catch(() => setPlayer(null));
    EvaluationsAPI.list(id).then(setEvals);
    VideosAPI.list(id).then(setVideos);
  }, [id]);

  const categories = useMemo(() => getCategoriesForPosition(player?.position), [player?.position]);
  const sortedEvals = useMemo(() => [...evals].sort((a, b) => new Date(b.date) - new Date(a.date)), [evals]);
  const latest = sortedEvals[0];
  const overall = latest ? getOverallScore(latest.ratings, categories) : 0;

  const latestCoach = useMemo(
    () => sortedEvals.find(e => (e.source || 'coach') === 'coach'),
    [sortedEvals]
  );
  const latestSelf = useMemo(
    () => sortedEvals.find(e => e.source === 'self'),
    [sortedEvals]
  );
  const compareCats = useMemo(() => {
    if (!latestCoach || !latestSelf) return [];
    return categories.map(c => {
      const coachVal = parseFloat(getCategoryScore(latestCoach.ratings, c.id, categories));
      const selfVal = parseFloat(getCategoryScore(latestSelf.ratings, c.id, categories));
      return {
        id: c.id,
        name: c.name,
        coach: coachVal,
        self: selfVal,
        gap: coachVal - selfVal,
      };
    });
  }, [latestCoach, latestSelf, categories]);

  const catData = latest ? categories.map(c => ({
    label: c.name, short: c.short || c.name.split(' ')[0],
    value: parseFloat(getCategoryScore(latest.ratings, c.id, categories))
  })) : [];

  const progressSeries = categories.map(c => ({
    id: c.id, name: c.name,
    data: sortedEvals.slice().reverse().map(e => ({ date: e.date, val: parseFloat(getCategoryScore(e.ratings, c.id, categories)) }))
  }));

  if (!player) return <div className="p-8 text-slate-500">Loading player</div>;

  const posBadge = player.position === 'Goalie'
    ? 'bg-violet-500/10 text-violet-700 border-violet-500/30'
    : player.position === 'Defense'
      ? 'bg-amber-500/10 text-amber-700 border-amber-500/30'
      : 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30';

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-5">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6">
        <div className="flex flex-wrap items-start gap-6">
          <img src={player.avatar} alt="" className="h-24 w-24 rounded-2xl ring-2 ring-slate-200" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-3xl font-bold text-slate-900">{player.firstName} {player.lastName}</h1>
              <span className="font-mono text-2xl text-slate-500">#{player.jerseyNumber}</span>
            </div>
            <div className="text-slate-500 mt-1">{player.team}</div>
            <div className="flex flex-wrap gap-2 mt-3">
              <Badge className={posBadge}>{player.position}</Badge>
              <Badge className="bg-slate-100 text-slate-600 border-slate-300">Shoots {player.shoots}</Badge>
              {player.height && <Badge className="bg-slate-100 text-slate-600 border-slate-300">{player.height}, {player.weight}</Badge>}
              <Badge className="bg-slate-100 text-slate-500 border-slate-300 text-[10px] uppercase tracking-wider">
                {player.position === 'Goalie' ? 'Goalie rubric' : 'Skater rubric'}
              </Badge>
            </div>
          </div>
          <div className="text-center">
            <div className="font-mono font-bold text-5xl bg-gradient-to-br from-sky-600 to-cyan-600 bg-clip-text text-transparent">
              {overall || '—'}
            </div>
            <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">Overall</div>
            <Button size="sm" onClick={() => navigate(`/app/evaluate/new?player=${player.id}`)}
              className="mt-3 bg-sky-500 hover:bg-sky-400 text-slate-900 font-semibold">
              <Plus className="h-3.5 w-3.5 mr-1" /> New Evaluation
            </Button>
            <Button size="sm" variant="outline" onClick={copyInviteLink}
              className="mt-2 border-slate-300 text-slate-700 hover:bg-slate-100 bg-transparent">
              {copied ? <Check className="h-3.5 w-3.5 mr-1 text-emerald-600" /> : <Link2 className="h-3.5 w-3.5 mr-1" />}
              {copied ? 'Copied!' : 'Copy Family Link'}
            </Button>
            <Button size="sm" variant="outline" onClick={copyProgressShare}
              data-testid="share-progress-btn"
              className="mt-2 border-slate-300 text-slate-700 hover:bg-slate-100 bg-transparent">
              <Share2 className="h-3.5 w-3.5 mr-1" /> Share Progress
            </Button>
            <Button size="sm" variant="ghost" onClick={revokeProgressShare}
              data-testid="revoke-progress-btn"
              className="mt-1 text-slate-500 hover:text-rose-600 text-xs">
              <X className="h-3 w-3 mr-1" /> Revoke share link
            </Button>
          </div>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="overview" className="data-[state=active]:bg-sky-500/15 data-[state=active]:text-sky-600">Overview</TabsTrigger>
          <TabsTrigger value="evaluations" className="data-[state=active]:bg-sky-500/15 data-[state=active]:text-sky-600">Evaluations ({sortedEvals.length})</TabsTrigger>
          <TabsTrigger value="videos" className="data-[state=active]:bg-sky-500/15 data-[state=active]:text-sky-600">Videos ({videos.length})</TabsTrigger>
          <TabsTrigger value="progress" className="data-[state=active]:bg-sky-500/15 data-[state=active]:text-sky-600">Progress</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-5 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h3 className="font-display font-semibold mb-1">Latest Evaluation Snapshot</h3>
              <p className="text-xs text-slate-500 mb-4">{latest ? latest.gameContext : 'No evaluation yet'}</p>
              {latest ? <RadarChart data={catData} /> : <div className="text-slate-500 text-sm py-8 text-center">No data yet.</div>}
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h3 className="font-display font-semibold mb-1">Category Breakdown</h3>
              <p className="text-xs text-slate-500 mb-4">Latest ratings across all {categories.length} categories</p>
              <div className="space-y-3">
                {catData.map(c => (
                  <div key={c.label}>
                    <div className="flex justify-between text-xs mb-1.5">
                      <span className="text-slate-600">{c.label}</span>
                      <span className="font-mono text-[#1e40af]">{c.value.toFixed(1)}</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#1e40af] to-[#1d4ed8] rounded-full" style={{ width: `${(c.value / 10) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Coach vs. Self side-by-side compare */}
          {latestCoach && latestSelf && (
            <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="coach-vs-self-card">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                <div>
                  <h3 className="font-display font-semibold">Coach vs. Self-Assessment</h3>
                  <p className="text-xs text-slate-500">
                    Coach: {latestCoach.gameContext}  {new Date(latestCoach.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    &nbsp;·&nbsp;
                    Self: {new Date(latestSelf.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </p>
                </div>
                <div className="text-xs text-slate-500">
                  Positive gap = coach sees higher than player
                </div>
              </div>
              <div className="space-y-3">
                {compareCats.map(c => {
                  const coachPct = (c.coach / 10) * 100;
                  const selfPct = (c.self / 10) * 100;
                  const gapClass =
                    Math.abs(c.gap) < 0.5 ? 'text-emerald-600' :
                    c.gap > 0 ? 'text-amber-600' : 'text-rose-600';
                  return (
                    <div key={c.id} data-testid={`compare-row-${c.id}`}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-slate-700 font-medium">{c.name}</span>
                        <span className={`font-mono ${gapClass}`}>{c.gap > 0 ? '+' : ''}{c.gap.toFixed(1)}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div className="flex justify-between text-[10px] text-slate-500 mb-0.5">
                            <span>Coach</span>
                            <span className="font-mono">{c.coach.toFixed(1)}</span>
                          </div>
                          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-[#0b1f4d] rounded-full" style={{ width: `${coachPct}%` }} />
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-[10px] text-slate-500 mb-0.5">
                            <span>Self</span>
                            <span className="font-mono">{c.self.toFixed(1)}</span>
                          </div>
                          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-[#1e40af] rounded-full" style={{ width: `${selfPct}%` }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="evaluations" className="mt-5 space-y-3">
          {sortedEvals.length === 0 && <div className="text-center py-12 text-slate-500">No evaluations yet.</div>}
          {sortedEvals.map(e => (
            <div key={e.id} className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-semibold text-slate-900">{e.gameContext}</div>
                  <div className="text-xs text-slate-500 mt-1 flex items-center gap-3">
                    <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(e.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    <span className="flex items-center gap-1"><User className="h-3 w-3" /> {e.evaluatorName}</span>
                  </div>
                  {e.notes && <p className="text-sm text-slate-500 mt-3 italic">{e.notes}</p>}
                </div>
                <div className="text-right">
                  <div className="font-mono font-bold text-2xl text-sky-600">{getOverallScore(e.ratings, categories)}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Overall</div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-2 md:grid-cols-5 gap-2">
                {categories.map(c => (
                  <div key={c.id} className="text-center bg-slate-50 rounded py-2 px-1">
                    <div className="text-[9px] uppercase tracking-wide text-slate-500 truncate">{c.short || c.name.split(' ')[0]}</div>
                    <div className="font-mono font-semibold text-sm text-slate-900">{getCategoryScore(e.ratings, c.id, categories)}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="videos" className="mt-5">
          {activeVideo && (
            <div className="mb-5">
              <VideoPlayer video={activeVideo} playerId={player.id} playerPosition={player.position} />
              <div className="mt-2 text-right">
                <Button variant="ghost" size="sm" onClick={() => setActiveVideo(null)} className="text-slate-500 hover:text-slate-900">Close player</Button>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {videos.map(v => <VideoCard key={v.id} video={v} onPlay={() => setActiveVideo(v)} />)}
            {videos.length === 0 && <div className="col-span-full text-center py-12 text-slate-500">No clips uploaded yet.</div>}
          </div>
        </TabsContent>

        <TabsContent value="progress" className="mt-5">
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="h-4 w-4 text-sky-600" />
              <h3 className="font-display font-semibold">Category Progress Over Time</h3>
            </div>
            <p className="text-xs text-slate-500 mb-6">Ratings across evaluations from oldest to newest</p>
            <ProgressChart series={progressSeries} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
